import { Award, BarChart3, BrainCircuit, Briefcase, Code2, Database, Mic2, Rocket } from "lucide-react";

const PHASE_ICONS = [Database, Code2, BarChart3, BrainCircuit, Rocket];
const OUTCOME_ICONS = [Award, Mic2, Briefcase];

const parseWeeks = (w) => {
  const [a, b] = String(w).split(/[-–]/).map((x) => parseInt(x, 10));
  return [a || 1, b || a || 1];
};

function PhaseCard({ m, i, total }) {
  const Icon = PHASE_ICONS[i % PHASE_ICONS.length];
  const [a, b] = parseWeeks(m.weeks);
  return (
    <li data-testid={`syllabus-phase-${i}`} className="relative pl-16 pb-5 last:pb-0">
      <span className="absolute left-0 top-1 w-10 h-10 rounded-xl bg-[#09090B] border border-amber-500/40 text-amber-400 flex items-center justify-center shadow-[0_0_24px_-6px_rgba(245,158,11,0.6)]">
        <Icon className="w-5 h-5" />
      </span>
      <div className="group rounded-2xl border border-zinc-800 bg-zinc-950/60 p-5 md:p-6 transition-[border-color,transform] duration-300 hover:border-amber-500/40 hover:translate-x-1">
        <div className="flex items-center justify-between gap-3">
          <span className="text-[10px] font-adult-mono uppercase tracking-[0.2em] text-zinc-500">Phase {String(i + 1).padStart(2, "0")} · Weeks {m.weeks}</span>
          <span className="font-adult-mono text-xs text-amber-400/80 shrink-0">{b - a + 1} wk</span>
        </div>
        <h3 className="mt-2 font-adult-heading text-xl">{m.module}</h3>
        <div className="mt-3 relative h-1.5 rounded-full bg-zinc-800/80 overflow-hidden" aria-hidden>
          <div className="absolute inset-y-0 rounded-full bg-gradient-to-r from-amber-500 to-violet-500" style={{ left: `${((a - 1) / total) * 100}%`, width: `${((b - a + 1) / total) * 100}%` }} />
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          {m.topics.map((t) => <span key={t} className="text-xs px-2.5 py-1 rounded-full border border-zinc-800 bg-zinc-900/60 text-zinc-300 font-adult-mono">{t}</span>)}
        </div>
      </div>
    </li>
  );
}

function WeekStrip({ syllabus, total }) {
  const phaseOf = (w) => syllabus.findIndex((m) => { const [a, b] = parseWeeks(m.weeks); return w >= a && w <= b; });
  return (
    <div aria-hidden className="mt-8 grid grid-cols-8 gap-1.5 max-w-xs">
      {Array.from({ length: total }, (_, k) => {
        const p = phaseOf(k + 1);
        return <span key={k} className={`h-6 rounded-md ${p % 2 === 0 ? "bg-amber-500/70" : p > 0 ? "bg-violet-500/60" : "bg-zinc-800"}`} style={{ opacity: 0.45 + (p + 1) * 0.11 }} />;
      })}
    </div>
  );
}

export function SyllabusRoadmap({ syllabus = [] }) {
  const total = Math.max(16, ...syllabus.map((m) => parseWeeks(m.weeks)[1]));
  return (
    <section data-testid="flagship-syllabus" className="max-w-7xl mx-auto px-6 py-14">
      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4 lg:sticky lg:top-24 self-start">
          <div className="text-[11px] uppercase tracking-[0.28em] text-zinc-500 font-adult-mono code-underline">Syllabus</div>
          <h2 className="mt-6 font-adult-heading text-3xl md:text-4xl font-semibold tracking-tight">Sixteen weekends. <span className="font-adult-serif italic text-amber-400">One shipping-ready analyst.</span></h2>
          <div className="mt-6 flex flex-wrap gap-2 text-xs font-adult-mono">
            <span className="rounded-full border border-amber-500/40 bg-amber-500/10 text-amber-300 px-3 py-1">{total} weeks</span>
            <span className="rounded-full border border-violet-500/40 bg-violet-500/10 text-violet-300 px-3 py-1">{syllabus.length} phases</span>
          </div>
          <WeekStrip syllabus={syllabus} total={total} />
        </div>
        <ol className="lg:col-span-8 relative">
          <span aria-hidden className="absolute left-5 top-4 bottom-4 w-px bg-gradient-to-b from-amber-500/70 via-violet-500/50 to-amber-500/10" />
          {syllabus.map((m, i) => <PhaseCard key={i} m={m} i={i} total={total} />)}
        </ol>
      </div>
    </section>
  );
}

function TrophyArt() {
  return (
    <svg viewBox="0 0 200 200" className="w-40 md:w-52 cws-float" aria-hidden>
      <defs>
        <linearGradient id="cws-trophy" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#FCD34D" /><stop offset="1" stopColor="#F59E0B" /></linearGradient>
        <linearGradient id="cws-trophy-base" x1="0" y1="0" x2="1" y2="0"><stop offset="0" stopColor="#8B5CF6" /><stop offset="1" stopColor="#6D28D9" /></linearGradient>
        <radialGradient id="cws-trophy-glow"><stop offset="0" stopColor="#F59E0B" stopOpacity="0.35" /><stop offset="1" stopColor="#F59E0B" stopOpacity="0" /></radialGradient>
      </defs>
      <circle cx="100" cy="100" r="92" fill="url(#cws-trophy-glow)" />
      <circle cx="100" cy="100" r="84" fill="none" stroke="#3f3f46" strokeDasharray="3 7" />
      <path d="M68 48h64v34a32 32 0 0 1-64 0z" fill="url(#cws-trophy)" />
      <path d="M68 56H54a15 15 0 0 0 16 24" fill="none" stroke="url(#cws-trophy)" strokeWidth="6" strokeLinecap="round" />
      <path d="M132 56h14a15 15 0 0 1-16 24" fill="none" stroke="url(#cws-trophy)" strokeWidth="6" strokeLinecap="round" />
      <text x="100" y="80" textAnchor="middle" fontFamily="monospace" fontWeight="700" fontSize="17" fill="#09090B">&lt;/&gt;</text>
      <rect x="93" y="112" width="14" height="18" fill="#D97706" />
      <rect x="74" y="130" width="52" height="12" rx="3" fill="url(#cws-trophy-base)" />
      <rect x="66" y="142" width="68" height="10" rx="3" fill="#3f3f46" />
      <path d="M40 46l3 7 7 3-7 3-3 7-3-7-7-3 7-3z" fill="#FCD34D" />
      <path d="M160 120l2 5 5 2-5 2-2 5-2-5-5-2 5-2z" fill="#A78BFA" />
      <path d="M150 40l2 4 4 2-4 2-2 4-2-4-4-2 4-2z" fill="#F59E0B" />
    </svg>
  );
}

export function GraduateWith({ outcomes = [] }) {
  return (
    <section className="max-w-7xl mx-auto px-6 pb-20">
      <div data-testid="flagship-outcomes" className="relative overflow-hidden rounded-3xl border border-amber-500/30 bg-gradient-to-br from-amber-500/10 via-zinc-950 to-violet-500/10 p-6 sm:p-8 md:p-12">
        <div className="absolute inset-0 grain pointer-events-none" />
        <div className="relative grid md:grid-cols-12 gap-8 items-center">
          <div className="md:col-span-8">
            <div className="text-[11px] uppercase tracking-[0.28em] text-amber-400 font-adult-mono">You graduate with</div>
            <h3 className="mt-3 font-adult-heading text-2xl md:text-3xl font-semibold tracking-tight">What you walk away with.</h3>
            <ul className="mt-6 grid sm:grid-cols-3 gap-4">
              {outcomes.map((o, i) => {
                const Icon = OUTCOME_ICONS[i % OUTCOME_ICONS.length];
                return (
                  <li key={i} data-testid={`flagship-outcome-${i}`} className="rounded-2xl border border-zinc-800 bg-black/30 p-5 transition-[transform,border-color] duration-300 hover:-translate-y-1 hover:border-amber-500/40">
                    <span className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/30 text-amber-400 flex items-center justify-center"><Icon className="w-5 h-5" /></span>
                    <p className="mt-4 text-sm text-zinc-200 leading-relaxed">{o}</p>
                  </li>
                );
              })}
            </ul>
          </div>
          <div className="md:col-span-4 flex justify-center"><TrophyArt /></div>
        </div>
      </div>
    </section>
  );
}
